/**
 * CommunityMessageReplyPreview — the reply-preview bubble rendered ABOVE the
 * main community message bubble when a message quotes another.
 *
 * Flutter source of truth:
 *  - `community_message_item.dart:72-83` — the `replyInfo` card (distinct
 *    `AppColors.replyMessageColor` background, narrower card width).
 *  - `:116-174` (`messageTypeText`, `isReply` branch) — the reply bubble shows
 *    `messageClearText` (the `previewText`) instead of the linkified text, with
 *    `userInfo(isReply: true)` directly below it.
 *  - `:263-268` (`messageClearText`) — renders `communityReply.previewText`.
 *  - `:101-114` (`messageContent`) — only the **text** reply branch is in scope;
 *    image/video replies route to `messageTypeMedia` (OUT OF SCOPE, task-prompt
 *    line 2). A non-text reply renders nothing here.
 *
 * Tap-target parity note (`community_message_item.dart:199-202`): Flutter's
 * `userInfo` `onTap` ALWAYS navigates to `message.messageUsersInfo.userId` (the
 * MAIN message author) — even inside the reply preview. The *name/avatar* come
 * from `communityReply.userId` / `.userName`, but the tap target is the main
 * author. This component mirrors Flutter exactly: `authorTapUserId` (the main
 * author) drives `Navigator.navigateToUserDetails`, NOT `communityReply.userId`.
 *
 * Theme / responsive tokens:
 *  - Bubble background → `theme.chat.replyMessage` (Flutter
 *    `AppColors.replyMessageColor = 0xff404040`).
 *  - Preview text → `theme.text.secondary`, italic, light weight, `font.sm`
 *    (Flutter `communityMessageReplyTextStyle`: `textColor`, 12, w300, italic —
 *    `app_styles.dart:1513-1519`).
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { CommunityChatMessageType } from '@/domain/enums/community/communityChatMessageType';
import type { CommunityReplyEntity } from '@/domain/entities/community/communityReplyEntity';
import { CommunityMessageAuthorInfo } from './CommunityMessageAuthorInfo';

export interface CommunityMessageReplyPreviewProps {
  /** The quoted-message snapshot embedded on the main message. */
  reply: CommunityReplyEntity;
  /** Current user's Firebase id — drives the `"You"` author label. */
  currentUserId: string;
  /**
   * The MAIN message author's id — the tap target for the author row, mirroring
   * Flutter (`community_message_item.dart:201`). NOT `reply.userId`.
   */
  authorTapUserId: string;
  /**
   * Preformatted main-message timestamp. Flutter's `userInfo(isReply: true)`
   * still renders `messageTimestamp()` (the MAIN message time) below the replied
   * author (`community_message_item.dart:225, 233-236`).
   */
  timestampLabel: string;
  /**
   * Whether the MAIN message is a gift. Flutter's `messageContent` text-branch
   * predicate (`community_message_item.dart:105-107`) is
   * `replyType == text.index || message.type == gift.index` — the gift check is
   * on the MAIN message type, NOT the reply type, so a gift main message renders
   * its reply preview as text regardless of the reply's own type.
   */
  mainMessageIsGift: boolean;
  /** Stable id of the parent message — used to scope `data-qa-*`. */
  messageId: string;
}

export const CommunityMessageReplyPreview: React.FC<CommunityMessageReplyPreviewProps> = ({
  reply,
  currentUserId,
  authorTapUserId,
  timestampLabel,
  mainMessageIsGift,
  messageId,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Flutter text-branch predicate (`community_message_item.dart:105-107`):
  // render text when `reply.type == text` OR the MAIN message is a gift. An
  // image/video reply (with a non-gift main message) routes to the media branch
  // (`:108-110`) which is out of scope (task-prompt line 2) — render nothing.
  const renderAsText =
    reply.type === CommunityChatMessageType.text || mainMessageIsGift;
  if (!renderAsText) {
    // TODO: @claude add a follow up task for this: media reply preview — Flutter lib/presentation/custom_widgets/community_message_item.dart:352-480
    return null;
  }

  return (
    <div
      {...qaAttr({ id: `community-message-reply-${messageId}` })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: responsive.spacing.xs,
        // Flutter narrows the reply card (`paddingHorizontal = 2 * 16`,
        // `cardWidth -= 4 * 16`); on the web we inset it from the main bubble.
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
      }}
    >
      <div
        style={{
          width: '100%',
          backgroundColor: theme.chat.replyMessage,
          borderRadius: responsive.borderRadius.md,
          padding: responsive.spacing.sm,
          color: theme.text.secondary,
          fontSize: responsive.font.sm,
          fontStyle: 'italic',
          fontWeight: responsive.fontWeight.regular,
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          boxSizing: 'border-box',
        }}
      >
        {reply.previewText ?? ''}
      </div>
      <CommunityMessageAuthorInfo
        authorUserId={reply.userId}
        authorUserName={reply.userName}
        currentUserId={currentUserId}
        tapUserId={authorTapUserId}
        timestampLabel={timestampLabel}
      />
    </div>
  );
};
